import { Directionality } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  NgZone,
  AfterViewInit,
  ChangeDetectorRef,
  Input,
  Optional,
  Self,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'sn-slide-toggle',
  templateUrl: './drag-slide-toggle.component.html',
  styleUrls: ['./drag-slide-toggle.component.scss'],
  host: {
    class: 'sn-slide-toggle',
  },
})
export class DragSlideToggleComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('toggleElement') public element: any;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(value: any) {
    if (value !== this._value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  @Input() public disabled: boolean;
  @Output() public readonly dragChange: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly toggleEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly toggleChange: EventEmitter<MatSlideToggleChange> =
    new EventEmitter<MatSlideToggleChange>();
  private _checked: boolean = false;
  private _value: any;
  private _dragging = false;
  private _previousChecked: boolean;
  private _thumbBarWidth: number;
  private _dragPercentage: number;
  private _thumbBarEl: any;
  private _thumbEl: any;
  private _inputEl: any;

  constructor(
    public elementRef: ElementRef,
    private readonly _ngZone: NgZone,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() private readonly _ngControl: NgControl,
    @Optional() private readonly _dir?: Directionality,
  ) {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
    }
  }

  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    this._checked = coerceBooleanProperty(value);
    this._changeDetectorRef.markForCheck();
  }

  public ngAfterViewInit(): void {
    this._thumbEl = this.elementRef.nativeElement.querySelector(
      '.mat-slide-toggle-thumb-container',
    );
    this._inputEl = this.elementRef.nativeElement.querySelector('.mat-slide-toggle-input');
    this._thumbBarEl = this.elementRef.nativeElement.querySelector('.mat-slide-toggle-bar');
  }

  public ngOnDestroy(): void {}

  public onChangeEvent(): void {
    if (!this._dragging) {
      this.toggleEvent.emit();
    }
    if (this._dragging) {
      this._inputEl.checked = this.checked;
      return;
    }
    this.checked = this._inputEl.checked;
    this._emitChangeEvent();
  }

  public onDragStart(): void {
    if (!this.disabled && !this._dragging) {
      const thumbEl = this._thumbEl;
      this._thumbBarWidth = this._thumbBarEl.clientWidth - thumbEl.clientWidth;
      thumbEl.classList.add('mat-dragging');

      this._previousChecked = this.checked;
      this._dragging = true;
    }
  }

  public onDrag(event: HammerInput): void {
    if (this._dragging) {
      const direction = this._dir && this._dir.value === 'rtl' ? -1 : 1;
      this._dragPercentage = this._getDragPercentage(event.deltaX * direction);
      const dragX = (this._dragPercentage / 100) * this._thumbBarWidth * direction;
      this._thumbEl.style.transform = `translate3d(${dragX}px, 0, 0)`;
    }
  }

  public onDragEnd(): void {
    if (this._dragging) {
      const newCheckedValue = this._dragPercentage > 50;

      if (newCheckedValue !== this.checked) {
        this.dragChange.emit();
        this.checked = newCheckedValue;
        this._emitChangeEvent();
      }

      this._ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          if (this._dragging) {
            this._dragging = false;
            this._thumbEl.classList.remove('mat-dragging');
            this._thumbEl.style.transform = '';
          }
        }),
      );
    }
  }

  public writeValue(value: any): void {
    this.checked = !!value;
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._touchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  private _emitChangeEvent(): void {
    this._onChange(this.checked);
    this.toggleChange.emit(new MatSlideToggleChange(this.element, this.checked));
  }

  private _getDragPercentage(distance: number): number {
    let percentage = (distance / this._thumbBarWidth) * 100;

    if (this._previousChecked) {
      percentage += 100;
    }
    return Math.max(0, Math.min(percentage, 100));
  }

  private _onChange = (_: any) => {};
  private readonly _changeFn: (value: any) => void = () => {};
  private _touchFn: () => any = () => {};
}
